import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { IoWallet } from "react-icons/io5";
import styles from "./navbar.module.css";
import {
  loadHome,
  loadOrders,
  loadProducts,
  loadSubscriptions,
} from "../../routeLoaders";

const Navbar = ({ onToggleSidebar }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("magizh_user") || "null");
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const updateUser = () => {
      try {
        setCurrentUser(JSON.parse(localStorage.getItem("magizh_user") || "null"));
      } catch {
        setCurrentUser(null);
      }
    };

    updateUser();

    const closeOnOutsideClick = (event) => {
      if (!event.target.closest("[data-profile-menu]")) {
        setIsProfileOpen(false);
      }
    };

    window.addEventListener("storage", updateUser);
    window.addEventListener("magizh-user-updated", updateUser);
    document.addEventListener("mousedown", closeOnOutsideClick);

    return () => {
      window.removeEventListener("storage", updateUser);
      window.removeEventListener("magizh-user-updated", updateUser);
      document.removeEventListener("mousedown", closeOnOutsideClick);
    };
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/home" && (location.pathname === "/" || location.pathname === "/home")) return true;
    return location.pathname === path;
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const userName = currentUser?.full_name || currentUser?.name || "Guest User";
  const userEmail = currentUser?.email || "Email not available";
  const userRole = currentUser?.role === "user" ? "user" : (currentUser?.role || "Professional Account");
  const userInitial = userName.charAt(0).toUpperCase();
  const walletBalance = Number(currentUser?.wallet_balance || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const profileMenu = (
    <div className={styles.profilePopover} role="dialog" aria-label="User profile details">
      <div className={styles.profilePopoverHeader}>
        <div className={styles.profilePopoverAvatar}>{userInitial}</div>
        <div className={styles.profilePopoverInfo}>
          <strong className={styles.profilePopoverName}>{userName}</strong>
          <span className={styles.profilePopoverEmail}>{userEmail}</span>
          <small className={styles.profilePopoverRole}>{userRole}</small>
        </div>
      </div>

      <hr className={styles.popoverDivider} />

      <Link
        to="/profile"
        className={styles.walletRow}
        onClick={() => setIsProfileOpen(false)}
      >
        <div className={styles.walletRowLeft}>
          <IoWallet className={styles.walletRowIcon} size={22} />
          <span className={styles.walletLabel}>Available Cashback</span>
        </div>
        <div className={styles.walletRowRight}>
          <strong className={styles.walletAmount}>₹ {walletBalance}</strong>
          <span className={styles.walletArrow}>›</span>
        </div>
      </Link>

      <hr className={styles.popoverDivider} />

      <Link to="/profile" className={styles.viewProfileBtn} onClick={() => setIsProfileOpen(false)}>
        View Profile
      </Link>
    </div>
  );

  const profileButton = (
    <div className={styles.profileMenuAnchor} data-profile-menu>
      <button
        type="button"
        className={styles.profileActionBtn}
        aria-label="Open profile details"
        aria-expanded={isProfileOpen}
        onClick={() => setIsProfileOpen((open) => !open)}
      >
        <span>{userInitial}</span>
      </button>
      {isProfileOpen && profileMenu}
    </div>
  );

  return (
    <header className={styles.navbar}>
      {/* Mobile-Only Header (Visible on screen width <= 768px) */}
      <div className={styles.mobileHeaderWrapper}>
        {/* Left: Hamburger button for Side Menu */}
        <button
          type="button"
          className={styles.mobileHamburgerBtn}
          onClick={onToggleSidebar}
          aria-label="Toggle Side Menu"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Center: Brand Logo */}
        <div className={styles.mobileLogoContainer}>
          <Link to="/home" className={styles.mobileBrandLogo}>
            <img
              src={`${process.env.PUBLIC_URL}/Assets/logo.png`}
              alt="Magizh Logo"
              style={{ height: "32px", objectFit: "contain" }}
            />
          </Link>
        </div>

        {/* Right: Wallet Button with Popover */}
        <div className={styles.profileMenuAnchor} data-profile-menu>
          <button
            type="button"
            className={styles.mobileWalletBtn}
            aria-label="Open wallet and profile details"
            aria-expanded={isProfileOpen}
            onClick={() => setIsProfileOpen((open) => !open)}
          >
            <IoWallet size={20} />
          </button>
          {isProfileOpen && profileMenu}
        </div>
      </div>

      {/* Desktop Container */}
      <div className={styles.navContainer}>
        {/* Left Group: Mobile Sidebar Trigger + Brand Logo */}
        <div className={styles.leftGroup}>
          {onToggleSidebar && (
            <button
              className={styles.sidebarToggleBtn}
              onClick={onToggleSidebar}
              title="Toggle Menu Drawer"
              aria-label="Toggle Menu Drawer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="17" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}

          <Link to="/home" className={styles.brandLogo} onClick={closeMobileMenu}>
            <img src={`${process.env.PUBLIC_URL}/Assets/logo.png`} alt="Magizh Logo" style={{ height: "38px", objectFit: "contain" }} />
          </Link>
        </div>

        {/* Center: Desktop Navigation Links */}
        <nav className={styles.navLinks}>
          <Link
            to="/home"
            onPointerEnter={loadHome}
            onFocus={loadHome}
            onTouchStart={loadHome}
            className={`${styles.navItem} ${isActive("/home") ? styles.active : ""}`}
          >
            Home
          </Link>
          <Link
            to="/subscriptions"
            onPointerEnter={loadSubscriptions}
            onFocus={loadSubscriptions}
            onTouchStart={loadSubscriptions}
            className={`${styles.navItem} ${isActive("/subscriptions") ? styles.active : ""}`}
          >
            Plans & Renewals
          </Link>
          <Link
            to="/products"
            onPointerEnter={loadProducts}
            onFocus={loadProducts}
            onTouchStart={loadProducts}
            className={`${styles.navItem} ${isActive("/products") || isActive("/product-details") ? styles.active : ""}`}
          >
            Products
          </Link>
          <Link
            to="/orders"
            onPointerEnter={loadOrders}
            onFocus={loadOrders}
            onTouchStart={loadOrders}
            className={`${styles.navItem} ${isActive("/orders") ? styles.active : ""}`}
          >
            Orders
          </Link>
          <Link
            to="/prime-member"
            className={`${styles.navItem} ${styles.primeNavItem} ${isActive("/prime-member") ? styles.active : ""}`}
          >
            <span className={styles.navCrownIcon}>👑</span> Prime Member
          </Link>
        </nav>

        {/* Right: Search, Actions & Hamburger */}
        <div className={styles.rightActions}>
          <div className={styles.searchBox}>
            <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search components..."
              className={styles.searchInput}
            />
          </div>

          {profileButton}

          {/* Hamburger menu toggle button for mobile */}
          <button
            className={styles.hamburgerBtn}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            {isMobileMenuOpen ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Slide-Down Drawer Menu */}
      {isMobileMenuOpen && (
        <div className={styles.mobileMenuDrawer}>
          <div className={styles.mobileSearchBox}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search..."
              className={styles.mobileSearchInput}
            />
          </div>

          <nav className={styles.mobileNavLinks}>
            <Link
              to="/home"
              className={`${styles.mobileNavItem} ${isActive("/home") ? styles.mobileActive : ""}`}
              onClick={closeMobileMenu}
            >
              Home
            </Link>
            <Link
              to="/subscriptions"
              className={`${styles.mobileNavItem} ${isActive("/subscriptions") ? styles.mobileActive : ""}`}
              onClick={closeMobileMenu}
            >
              Plans & Renewals
            </Link>
            <Link
              to="/products"
              className={`${styles.mobileNavItem} ${isActive("/products") || isActive("/product-details") ? styles.mobileActive : ""}`}
              onClick={closeMobileMenu}
            >
              Products
            </Link>
            <Link
              to="/orders"
              className={`${styles.mobileNavItem} ${isActive("/orders") ? styles.mobileActive : ""}`}
              onClick={closeMobileMenu}
            >
              Orders
            </Link>
            <Link
              to="/prime-member"
              className={`${styles.mobileNavItem} ${styles.mobilePrimeNavItem} ${isActive("/prime-member") ? styles.mobileActive : ""}`}
              onClick={closeMobileMenu}
            >
              👑 Prime Member
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
